import { useEffect, useRef, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { EmailBlockRenderer } from './EmailBlockRenderer';
import { isEmptyRichHtml } from './emailCompiler';

const CANVAS_QUILL_MODULES = {
  toolbar: [
    [{ size: ['12px', '14px', '16px', '18px', '24px', '32px'] }],
    ['bold', 'italic', 'underline'],
    [{ color: [] }, { align: [] }],
  ],
};

function CanvasTextEditor({ block, blockIndex, active, onChange, onActivate }) {
  const ref = useRef(null);
  const placeholder =
    blockIndex === 0 ? 'Start writing your email...' : 'Continue writing...';

  useEffect(() => {
    if (!active) return;
    const current = document.activeElement;
    const tag = current?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
    if (current?.closest?.('aside')) return;
    if (current?.closest?.('.canvas-quill')) return;
    ref.current?.getEditor?.()?.focus();
  }, [active, block.id]);

  return (
    <div
      className={`canvas-quill ${active ? 'is-active' : ''}`}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <ReactQuill
        ref={ref}
        theme="snow"
        value={block.content?.html || ''}
        placeholder={placeholder}
        onFocus={onActivate}
        onChange={(html, _delta, source) => {
          if (source !== 'user') return;
          if (html === (block.content?.html || '')) return;
          onChange(block.id, { ...block.content, html });
        }}
        modules={CANVAS_QUILL_MODULES}
      />
    </div>
  );
}

export function EmailCanvas({
  blocks,
  selectedBlockId,
  onSelectBlock,
  onMoveBlock,
  onDeleteBlock,
  onInsertBlockAt,
  onReorderBlocks,
  onUpdateBlock,
  onStartWriting,
  mode = 'edit',
}) {
  const [dragOverIndex, setDragOverIndex] = useState(null); // null | number

  // Handle Drag & Drop from toolbar or canvas reorder
  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    if (dragOverIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = (e) => {
    // Only reset if leaving the container
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverIndex(null);
    }
  };

  const handleDrop = (e, index) => {
    e.preventDefault();
    setDragOverIndex(null);

    // 1. Check if dragging from bottom toolbar
    const blockType =
      e.dataTransfer.getData('text/email-block-type') ||
      e.dataTransfer.getData('text/plain');

    if (blockType && onInsertBlockAt) {
      onInsertBlockAt(blockType, index);
      return;
    }

    // 2. Check if reordering an existing block
    const fromIndexStr = e.dataTransfer.getData('text/email-block-index');
    if (fromIndexStr !== '' && fromIndexStr !== null && onReorderBlocks) {
      const fromIndex = parseInt(fromIndexStr, 10);
      if (!Number.isNaN(fromIndex)) {
        onReorderBlocks(fromIndex, index);
      }
    }
  };

  const handleBlockDragStart = (e, index) => {
    e.dataTransfer.setData('text/email-block-index', String(index));
    e.dataTransfer.effectAllowed = 'move';
  };

  return (
    <div className="w-full flex-1 bg-gray-50 overflow-hidden flex flex-col rounded-2xl border border-dashed border-gray-300 h-full min-h-0 font-sf-pro-display relative">
      <style>{`
        .canvas-quill .ql-toolbar.ql-snow { display: none; border: none; padding: 4px 8px; }
        .canvas-quill.is-active .ql-toolbar.ql-snow {
          display: flex; flex-wrap: nowrap; overflow-x: auto; overflow-y: hidden;
          max-width: 100%; border-bottom: 1px solid #e5e7eb;
          -webkit-overflow-scrolling: touch; touch-action: pan-x;
        }
        .canvas-quill .ql-formats { flex-shrink: 0; }
        .canvas-quill .ql-container.ql-snow { border: none; font-size: 14px; }
        .canvas-quill .ql-editor { min-height: 72px; padding: 12px 16px; }
        .canvas-quill .ql-editor.ql-blank::before { color: #9ca3af; font-style: normal; }
        .canvas-quill .ql-snow .ql-picker.ql-size { width: 68px; }
        .canvas-quill .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="12px"]::before,
        .canvas-quill .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="12px"]::before { content: '12px'; }
        .canvas-quill .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="14px"]::before,
        .canvas-quill .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="14px"]::before { content: '14px'; }
        .canvas-quill .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="16px"]::before,
        .canvas-quill .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="16px"]::before { content: '16px'; }
        .canvas-quill .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="18px"]::before,
        .canvas-quill .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="18px"]::before { content: '18px'; }
        .canvas-quill .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="24px"]::before,
        .canvas-quill .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="24px"]::before { content: '24px'; }
        .canvas-quill .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="32px"]::before,
        .canvas-quill .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="32px"]::before { content: '32px'; }
      `}</style>
      {/* Scrollable Canvas Viewport */}
      <div
        className="flex-1 overflow-y-auto overflow-x-hidden flex justify-center items-start pt-6 pb-4 px-2 sm:pt-10 sm:pb-6 sm:px-4 xl:pb-8 xl:px-6 relative min-w-0 w-full max-w-full custom-scrollbar cursor-default"
        onClick={() => mode === 'edit' && onSelectBlock?.(null)}
      >
        <div
          className="w-full max-w-[620px] min-w-0 bg-white shadow-sm border border-gray-100 rounded-xl min-h-[280px] sm:min-h-[580px] h-fit relative transition-all"
          onClick={(e) => {
            // Deselect block if clicking on whitespace in the canvas sheet
            if (e.target === e.currentTarget && mode === 'edit') {
              onSelectBlock?.(null);
            }
          }}
          onDragOver={(e) => {
            if (blocks.length === 0) handleDragOver(e, 0);
          }}
          onDrop={(e) => {
            if (blocks.length === 0) handleDrop(e, 0);
          }}
        >
          {mode === 'edit' ? (
            blocks.length === 0 ? (
              <button
                type="button"
                className={`min-h-[160px] w-full px-6 pt-6 text-left text-sm text-gray-400 ${
                  dragOverIndex === 0 ? 'bg-surface-mint/20' : ''
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  onStartWriting?.(0);
                }}
              >
                Start writing your email...
              </button>
            ) : (
              <div
                className="flex flex-col w-full relative"
                onDragLeave={handleDragLeave}
              >
                {blocks.map((block, idx) => {
                  const isText =
                    block.type === 'TEXT' || block.type === 'HEADING';
                  const isEditing = selectedBlockId === block.id;
                  const isSelected = isEditing;
                  const isDropIndicator = dragOverIndex === idx;

                  return (
                    <div
                      key={block.id}
                      draggable={!isText}
                      onDragStart={(e) => {
                        if (isText) {
                          e.preventDefault();
                          return;
                        }
                        handleBlockDragStart(e, idx);
                      }}
                      onDragOver={(e) => handleDragOver(e, idx)}
                      onDrop={(e) => handleDrop(e, idx)}
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectBlock(block.id);
                      }}
                      className={`relative group transition-all duration-150 ${
                        isDropIndicator ? 'border-t-2 border-primary' : ''
                      } ${
                        isSelected
                          ? 'outline outline-1 outline-primary z-10'
                          : 'hover:outline hover:outline-1 hover:outline-gray-300'
                      }`}
                    >
                      {isSelected && (
                        <div
                          className="absolute right-1 top-0 z-20 flex -translate-y-full items-center rounded-md border border-gray-200 bg-white shadow-sm"
                          onClick={(e) => e.stopPropagation()}
                          onMouseDown={(e) => e.stopPropagation()}
                        >
                          <button
                            type="button"
                            draggable
                            onDragStart={(e) => handleBlockDragStart(e, idx)}
                            className="flex h-8 w-8 cursor-grab items-center justify-center rounded-l-md text-gray-500 hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary active:cursor-grabbing"
                            title="Drag to reorder"
                            aria-label="Drag to reorder"
                          >
                            <svg
                              className="h-4 w-4"
                              viewBox="0 0 16 16"
                              fill="currentColor"
                              aria-hidden="true"
                            >
                              <circle cx="5" cy="3" r="1.2" />
                              <circle cx="11" cy="3" r="1.2" />
                              <circle cx="5" cy="8" r="1.2" />
                              <circle cx="11" cy="8" r="1.2" />
                              <circle cx="5" cy="13" r="1.2" />
                              <circle cx="11" cy="13" r="1.2" />
                            </svg>
                          </button>
                          <span className="h-4 w-px bg-gray-200" />
                          <button
                            type="button"
                            onClick={() => onMoveBlock(block.id, 'up')}
                            disabled={idx === 0}
                            className="flex h-8 w-8 items-center justify-center text-gray-500 hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary disabled:cursor-not-allowed disabled:opacity-30"
                            title="Move up"
                            aria-label="Move up"
                          >
                            <svg
                              className="h-4 w-4"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M5 15l7-7 7 7"
                              />
                            </svg>
                          </button>
                          <button
                            type="button"
                            onClick={() => onMoveBlock(block.id, 'down')}
                            disabled={idx === blocks.length - 1}
                            className="flex h-8 w-8 items-center justify-center text-gray-500 hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary disabled:cursor-not-allowed disabled:opacity-30"
                            title="Move down"
                            aria-label="Move down"
                          >
                            <svg
                              className="h-4 w-4"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M19 9l-7 7-7-7"
                              />
                            </svg>
                          </button>
                          <span className="h-4 w-px bg-gray-200" />
                          <button
                            type="button"
                            onClick={() => onDeleteBlock(block.id)}
                            className="flex h-8 w-8 items-center justify-center rounded-r-md text-gray-500 hover:bg-red-50 hover:text-red-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
                            title="Delete block"
                            aria-label="Delete block"
                          >
                            <svg
                              className="h-4 w-4"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                          </button>
                        </div>
                      )}

                      <div
                        className={
                          isText
                            ? 'w-full'
                            : 'pointer-events-none w-full select-none'
                        }
                      >
                        {isText ? (
                          <CanvasTextEditor
                            block={block}
                            blockIndex={idx}
                            active={isEditing}
                            onChange={onUpdateBlock}
                            onActivate={() => {
                              if (selectedBlockId !== block.id)
                                onSelectBlock(block.id);
                            }}
                          />
                        ) : (
                          <EmailBlockRenderer block={block} />
                        )}
                      </div>
                    </div>
                  );
                })}

                {blocks.length > 0 &&
                  !(
                    blocks[blocks.length - 1]?.type === 'TEXT' &&
                    isEmptyRichHtml(blocks[blocks.length - 1]?.content?.html)
                  ) && (
                    <button
                      type="button"
                      className="w-full px-4 py-3 text-left text-sm text-gray-400 hover:text-gray-500"
                      onClick={(e) => {
                        e.stopPropagation();
                        onStartWriting?.(blocks.length);
                      }}
                    >
                      Continue writing...
                    </button>
                  )}

                {/* Bottom Drop Zone when dragging below last item */}
                <div
                  onDragOver={(e) => handleDragOver(e, blocks.length)}
                  onDrop={(e) => handleDrop(e, blocks.length)}
                  className={`h-8 w-full transition-colors flex items-center justify-center ${
                    dragOverIndex === blocks.length
                      ? 'border-b-2 border-primary bg-surface-mint/30'
                      : ''
                  }`}
                />
              </div>
            )
          ) : (
            <div className="flex flex-col w-full">
              {blocks.length === 0 ? (
                <div className="min-h-[580px] flex items-center justify-center">
                  <p className="text-gray-400 text-sm">No blocks to preview</p>
                </div>
              ) : (
                blocks.map((block) => (
                  <div key={block.id} className="w-full">
                    <EmailBlockRenderer block={block} />
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
